import type { Component } from '../router';
import { navigate } from '../router';
import { icon } from '../components/icons';
import { ViewportGate } from '../components/ViewportGate';
import { confirmDialog } from '../components/ConfirmDialog';
import { ApiError } from '../services/api';
import { currentSessionId, logout } from '../services/auth';
import {
  listSessions,
  revokeSession,
  reportDeviceCompromised,
  signOutOtherDevices,
  type SessionView,
} from '../services/sessions';

/**
 * Devices & sessions — account-security screen. Lists the user's active
 * sessions and lets them revoke any device. Under the hard device-cap policy
 * (no auto-evict), revoking here is the *only* way to free a slot so a new
 * device can sign in.
 *
 * Dual UX (viewport-adaptive, not stretched): phone renders a compact card
 * list; desktop renders a dense table. The current device cannot be revoked
 * via DELETE (the backend returns 403 CANNOT_REVOKE_CURRENT_SESSION), so its
 * row routes through POST /api/auth/logout and bounces to /login — and that
 * destructive self-logout is gated behind a confirmation (AC3).
 */

type Layout = 'phone' | 'desktop';

// Relative "last used" label. Pure + now-injected so it's deterministic
// under test.
export function formatLastUsed(now: Date, iso: string): string {
  const then = new Date(iso);
  const ms = now.getTime() - then.getTime();
  if (Number.isNaN(ms)) return 'Unknown';
  const sec = Math.floor(ms / 1000);
  if (sec < 45) return 'Active now';
  const min = Math.floor(sec / 60);
  if (min < 60) return min <= 1 ? '1 minute ago' : `${min} minutes ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr <= 1 ? '1 hour ago' : `${hr} hours ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day} days ago`;
  return then.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function revokeErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'That session was already signed out.';
    if (err.status === 403) return 'That’s the device you’re using now — use “Sign out this device” instead.';
    if (err.status === 429) return 'Too many attempts — give it a minute.';
    return 'Something went wrong. Please try again.';
  }
  return 'Could not reach the server. Check your connection.';
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

export class SessionsView implements Component {
  private root: HTMLElement | null = null;
  private listHost: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private footer: HTMLElement | null = null;
  private sessions: SessionView[] = [];
  private reported = new Set<string>();
  private controller: AbortController | null = null;
  private destroyed = false;

  constructor(private layout: Layout) {}

  mount(host: HTMLElement): void {
    const section = node('section', 'sessions');
    section.dataset.layout = this.layout;

    const header = node('header', 'sessions__header');
    // Phone reaches this screen from the Me hub — give it a way back.
    // Desktop has the persistent top nav, so no back affordance needed.
    if (this.layout === 'phone') {
      const back = node('a', 'sessions__back');
      back.href = '/me';
      const chevron = icon('chevron-right');
      chevron.classList.add('sessions__back-chevron');
      back.append(chevron, document.createTextNode('Me'));
      header.append(back);
    }
    header.append(
      node('h1', 'sessions__title', 'Devices & sessions'),
      node(
        'p',
        'sessions__sub',
        "These are the devices currently signed in to your account. Revoke any you don't recognise.",
      ),
    );

    const status = node('p', 'sessions__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.dataset.visible = 'false';
    this.statusEl = status;

    const list = node('div', 'sessions__list');
    this.listHost = list;

    const footer = node('div', 'sessions__footer');
    this.footer = footer;

    section.append(header, status, list, footer);
    host.append(section);
    this.root = section;

    void this.load();
  }

  unmount(): void {
    this.destroyed = true;
    this.controller?.abort();
    this.controller = null;
    this.root?.remove();
    this.root = null;
  }

  private async load(): Promise<void> {
    this.renderLoading();
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const sessions = await listSessions(this.controller.signal);
      if (this.destroyed) return;
      // The server can't flag the current session here (its refresh-token
      // cookie is path-scoped to /api/auth), so derive it from the access
      // token's `sid`. This drives the "This device" badge and routes the
      // current row to logout instead of a self-DELETE.
      const sid = currentSessionId();
      this.sessions = sid == null ? sessions : sessions.map((s) => ({ ...s, current: s.id === sid }));
      this.renderList();
    } catch (err) {
      if (this.destroyed || (err instanceof DOMException && err.name === 'AbortError')) return;
      this.renderError();
    }
  }

  private renderLoading(): void {
    if (!this.listHost) return;
    this.listHost.replaceChildren(node('p', 'sessions__placeholder', 'Loading your sessions…'));
    this.footer?.replaceChildren();
  }

  private renderError(): void {
    if (!this.listHost) return;
    const box = node('div', 'sessions__error');
    box.append(node('p', 'sessions__placeholder', 'Could not load your sessions. Check your connection.'));
    const retry = node('button', 'sessions__retry', 'Try again');
    retry.type = 'button';
    retry.addEventListener('click', () => void this.load());
    box.append(retry);
    this.listHost.replaceChildren(box);
    this.footer?.replaceChildren();
  }

  private renderList(): void {
    if (!this.listHost) return;
    if (this.sessions.length === 0) {
      // Defensive: a signed-in user always has at least the current session,
      // but never render an empty void.
      this.listHost.replaceChildren(
        node('p', 'sessions__placeholder', 'No active sessions found.'),
      );
      this.footer?.replaceChildren();
      return;
    }

    const container = this.layout === 'phone' ? this.buildCards() : this.buildTable();
    this.listHost.replaceChildren(container);
    this.renderFooter();
  }

  private buildCards(): HTMLElement {
    const now = new Date();
    const ul = node('ul', 'sessions__cards');
    for (const s of this.sessions) {
      const li = node('li', 'session-card');
      li.dataset.current = String(s.current);

      const head = node('div', 'session-card__head');
      head.append(node('span', 'session-card__device', deviceLabel(s)));
      if (s.current) head.append(node('span', 'session-card__badge', 'This device'));

      const meta = node('dl', 'session-card__meta');
      meta.append(
        metaPair('Last used', formatLastUsed(now, s.lastUsedAt)),
        metaPair('IP address', ipLabel(s)),
      );

      li.append(head, meta, this.actions(s, 'session-card'));
      ul.append(li);
    }
    return ul;
  }

  private buildTable(): HTMLElement {
    const now = new Date();
    const table = node('table', 'sessions__table');

    const thead = node('thead');
    const htr = node('tr');
    for (const label of ['Device', 'Last used', 'IP address', 'Signed in']) {
      const th = node('th', undefined, label);
      th.scope = 'col';
      htr.append(th);
    }
    const actionTh = node('th');
    actionTh.scope = 'col';
    actionTh.append(node('span', 'sessions__sr-only', 'Actions'));
    htr.append(actionTh);
    thead.append(htr);

    const tbody = node('tbody');
    for (const s of this.sessions) {
      const tr = node('tr', 'session-row');
      tr.dataset.current = String(s.current);

      const device = node('td', 'session-row__device');
      device.append(node('span', undefined, deviceLabel(s)));
      if (s.current) device.append(node('span', 'session-row__badge', 'This device'));

      tr.append(
        device,
        node('td', undefined, formatLastUsed(now, s.lastUsedAt)),
        node('td', 'session-row__ip', ipLabel(s)),
        node('td', undefined, formatLastUsed(now, s.createdAt)),
      );
      const action = node('td', 'session-row__action');
      action.append(this.actions(s, 'session-row'));
      tr.append(action);
      tbody.append(tr);
    }

    table.append(thead, tbody);
    return table;
  }

  // actions builds the per-session control cluster: once reported, both buttons
  // give way to a status note; otherwise Revoke (or "Sign out this device" for
  // the current row) plus, for any other device, the destructive "This wasn't
  // me" report.
  private actions(s: SessionView, base: string): HTMLElement {
    const wrap = node('div', `${base}__actions`);
    if (this.reported.has(s.id)) {
      wrap.append(
        node('span', `${base}__reported`, 'Reported — will be signed out and wiped when it next connects'),
      );
      return wrap;
    }
    wrap.append(this.revokeButton(s, `${base}__revoke`));
    // Reporting your current device would wipe and lock you out mid-action;
    // that path is "Sign out this device". Report is for the others.
    if (!s.current) wrap.append(this.reportButton(s, `${base}__report`));
    return wrap;
  }

  private revokeButton(s: SessionView, className: string): HTMLButtonElement {
    const btn = node('button', `btn-revoke ${className}`, s.current ? 'Sign out this device' : 'Revoke');
    btn.type = 'button';
    btn.dataset.id = s.id;
    if (s.current) btn.dataset.current = 'true';
    btn.setAttribute('aria-label', s.current ? 'Sign out this device' : `Revoke ${deviceLabel(s)}`);
    btn.addEventListener('click', () => void this.onRevoke(s, btn));
    return btn;
  }

  private reportButton(s: SessionView, className: string): HTMLButtonElement {
    const btn = node('button', `btn-report ${className}`, "This wasn't me");
    btn.type = 'button';
    btn.dataset.id = s.id;
    btn.setAttribute('aria-label', `Report ${deviceLabel(s)} as not mine`);
    btn.addEventListener('click', () => void this.onReport(s, btn));
    return btn;
  }

  private renderFooter(): void {
    if (!this.footer) return;
    const others = this.sessions.filter((s) => !s.current);
    if (others.length === 0) {
      this.footer.replaceChildren();
      return;
    }
    const btn = node('button', 'sessions__signout-others', 'Sign out all other devices');
    btn.type = 'button';
    btn.addEventListener('click', () => void this.onSignOutOthers(others.length, btn));
    this.footer.replaceChildren(btn);
  }

  private async onRevoke(s: SessionView, btn: HTMLButtonElement): Promise<void> {
    if (s.current) {
      // Revoking the current device = signing yourself out here. Gated by a
      // confirmation (AC3) and routed through logout, since DELETE on the
      // current session 403s.
      const ok = await confirmDialog({
        title: 'Sign out this device?',
        message:
          "You'll be signed out here and returned to the sign-in screen. Your other devices stay signed in.",
        confirmLabel: 'Sign out',
        cancelLabel: 'Stay signed in',
        tone: 'danger',
      });
      if (!ok || this.destroyed) return;
      try {
        await logout();
      } finally {
        navigate('/login');
      }
      return;
    }

    btn.disabled = true;
    btn.dataset.pending = 'true';
    try {
      await revokeSession(s.id);
      if (this.destroyed) return;
      this.sessions = this.sessions.filter((x) => x.id !== s.id);
      this.renderList();
      this.setStatus(`Signed out ${deviceLabel(s)}.`, 'success');
    } catch (err) {
      if (this.destroyed) return;
      btn.disabled = false;
      delete btn.dataset.pending;
      this.setStatus(revokeErrorCopy(err), 'error');
    }
  }

  // onReport flags another device compromised ("this wasn't me"). The
  // server tombstones the device's sessions — the kill + offline-data wipe land
  // when it next connects — so the row stays, marked reported, rather than
  // vanishing. Gated by a destructive confirmation.
  private async onReport(s: SessionView, btn: HTMLButtonElement): Promise<void> {
    const ok = await confirmDialog({
      title: "Report this device?",
      message:
        "We'll sign this device out and erase anything it saved offline the next time it connects, and it'll have to set up a passkey again. Only do this if you don't recognise it.",
      confirmLabel: 'Report device',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok || this.destroyed) return;

    btn.disabled = true;
    btn.dataset.pending = 'true';
    try {
      await reportDeviceCompromised(s.id);
      if (this.destroyed) return;
      this.reported.add(s.id);
      this.renderList();
      this.setStatus(
        `Reported ${deviceLabel(s)}. It'll be signed out and its offline data wiped the next time it connects.`,
        'success',
      );
    } catch (err) {
      if (this.destroyed) return;
      btn.disabled = false;
      delete btn.dataset.pending;
      this.setStatus(revokeErrorCopy(err), 'error');
    }
  }

  private async onSignOutOthers(count: number, btn: HTMLButtonElement): Promise<void> {
    const ok = await confirmDialog({
      title: 'Sign out all other devices?',
      message:
        count === 1
          ? "Your one other device will be signed out. This device stays signed in."
          : `All ${count} of your other devices will be signed out. This device stays signed in.`,
      confirmLabel: 'Sign out others',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok || this.destroyed) return;

    btn.disabled = true;
    btn.dataset.pending = 'true';
    try {
      const { revoked, failed } = await signOutOtherDevices(this.sessions);
      if (this.destroyed) return;
      if (failed > 0) {
        this.setStatus(
          `Signed out ${revoked} device${revoked === 1 ? '' : 's'}; ${failed} could not be signed out.`,
          'error',
        );
      } else {
        this.setStatus(
          `Signed out ${revoked} other device${revoked === 1 ? '' : 's'}.`,
          'success',
        );
      }
      await this.load();
    } catch (err) {
      if (this.destroyed) return;
      btn.disabled = false;
      delete btn.dataset.pending;
      this.setStatus(revokeErrorCopy(err), 'error');
    }
  }

  private setStatus(message: string, tone: 'success' | 'error'): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.dataset.tone = tone;
    this.statusEl.dataset.visible = 'true';
  }
}

function deviceLabel(s: SessionView): string {
  return s.deviceName.trim() || 'Unknown device';
}

function ipLabel(s: SessionView): string {
  return s.ipAddress.trim() || 'Unknown';
}

function metaPair(term: string, value: string): HTMLElement {
  const wrap = node('div', 'session-card__meta-row');
  wrap.append(node('dt', undefined, term), node('dd', undefined, value));
  return wrap;
}

export function SessionsPage(): Component {
  return new ViewportGate({
    phone: () => new SessionsView('phone'),
    desktop: () => new SessionsView('desktop'),
  });
}
